import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Avatar,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Person, Refresh, Warning, ChevronLeft, ChevronRight, Visibility } from '@mui/icons-material';
import { fetchUsers, createUser, updateUser, promoteUser, demoteUser, deleteUser, clearError } from '../../store/slices/userSlice';
import { getUser, getUserAcquisitionAnalytics } from '../../services/user';
import { getStates, getLGAs, getCitiesByLGA } from '../../services/location';
import PageMeta from '../../components/PageMeta';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend } from 'recharts';

const defaultForm = {
  username: '',
  email: '',
  fullname: '',
  phone: '',
  position: '',
  role: 'customer',
  is_active: true,
  state_id: '',
  lga_id: '',
  city_id: ''
};

  const Users = () => {
  const { t } = useTranslation(['users', 'common']);
  const dispatch = useDispatch();
  const { users, loading, error, totalUsers } = useSelector((state) => state.user);
  
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [originalRole, setOriginalRole] = useState('customer');
  const [form, setForm] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [detailsModal, setDetailsModal] = useState({ open: false, user: null, loading: false });
  
  // Location states
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  
  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, userId: null, userName: '' });
  
  // Filter states for dynamic search
  const [filters, setFilters] = useState({
    username: '',
    email: '',
    phone: '',
    role: '',
    status: ''
  });
  const [acquisitionAnalytics, setAcquisitionAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('monthly');

  // Custom debounce hook
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    
    useEffect(() => {
      console.log('⏰ Debounce effect triggered:', { value, delay, debouncedValue });
      const handler = setTimeout(() => {
        console.log('⏰ Debounce timeout fired, setting value:', value);
        setDebouncedValue(value);
      }, delay);
      
      return () => {
        console.log('⏰ Debounce cleanup');
        clearTimeout(handler);
      };
    }, [value, delay]);
    
    return debouncedValue;
  };

  // Individual debounced values to avoid object recreation
  const debouncedUsername = useDebounce(filters.username, 500);
  const debouncedEmail = useDebounce(filters.email, 500);
  const debouncedPhone = useDebounce(filters.phone, 500);
  const debouncedRole = useDebounce(filters.role, 500);
  const debouncedStatus = useDebounce(filters.status, 500);

  // Pagination state
  const [page, setPage] = useState(0); // 0-based for frontend, 1-based for backend
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  // Calculate total pages from totalUsers
  const totalPages = Math.ceil(totalUsers / rowsPerPage) || 1;


  const fetchUsersData = useCallback((pageArg, rowsArg, filterParams = {}) => {
    console.log('🚀 fetchUsersData called:', { pageArg, rowsArg, filterParams });
    
    // Only include non-empty filters
    const cleanFilters = {};
    Object.entries(filterParams).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        cleanFilters[key] = value;
      }
    });

    console.log('🚀 Dispatching fetchUsers with:', { cleanFilters, page: pageArg + 1, page_size: rowsArg });
    
    dispatch(fetchUsers({
      ...cleanFilters,
      page: pageArg + 1, // backend expects 1-based
      page_size: rowsArg,
      ordering: '-created_at',
    }));
  }, [dispatch]);

  // Auto-apply filters when debounced values change
  useEffect(() => {
    const debouncedFilters = {
      username: debouncedUsername,
      email: debouncedEmail,
      phone: debouncedPhone,
      role: debouncedRole,
      status: debouncedStatus
    };
    console.log('🔍 Filter effect triggered:', { debouncedFilters, rowsPerPage });
    setPage(0); // Reset to first page when filters change
    fetchUsersData(0, rowsPerPage, debouncedFilters);
  }, [debouncedUsername, debouncedEmail, debouncedPhone, debouncedRole, debouncedStatus, rowsPerPage, fetchUsersData]);

  // Handle pagination changes (only when page > 0 to avoid double fetch)
  useEffect(() => {
    console.log('🔍 Page effect triggered:', { page, rowsPerPage });
    if (page > 0) {
      const debouncedFilters = {
        username: debouncedUsername,
        email: debouncedEmail,
        phone: debouncedPhone,
        role: debouncedRole,
        status: debouncedStatus
      };
      fetchUsersData(page, rowsPerPage, debouncedFilters);
    }
  }, [page, fetchUsersData, rowsPerPage, debouncedUsername, debouncedEmail, debouncedPhone, debouncedRole, debouncedStatus]);

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  // Refresh function with current filters
  const handleRefresh = () => {
    const debouncedFilters = {
      username: debouncedUsername,
      email: debouncedEmail,
      phone: debouncedPhone,
      role: debouncedRole,
      status: debouncedStatus
    };
    setPage(0);
    fetchUsersData(0, rowsPerPage, debouncedFilters);
    loadAcquisitionAnalytics();
  };

  const loadAcquisitionAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const data = await getUserAcquisitionAnalytics();
      setAcquisitionAnalytics(data);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to load user acquisition analytics', severity: 'error' });
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  // Filter change handler
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      username: '',
      email: '',
      phone: '',
      role: '',
      status: ''
    });
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(filter => filter && filter.trim() !== '');
  const totals = acquisitionAnalytics?.totals || {};
  const comparisons = acquisitionAnalytics?.comparisons || {};
  const activeSeries = acquisitionAnalytics?.series?.[analyticsPeriod] || [];

  const formatPercent = (value) => {
    const numberValue = Number(value || 0);
    if (numberValue > 0) return `+${numberValue}%`;
    return `${numberValue}%`;
  };

  const getComparisonColor = (value) => {
    const numberValue = Number(value || 0);
    if (numberValue > 0) return 'success.main';
    if (numberValue < 0) return 'error.main';
    return 'text.secondary';
  };

  const chartData = activeSeries.map((item) => ({
    label: item.period,
    signups: item.count,
  }));



  useEffect(() => {
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    loadAcquisitionAnalytics();
  }, [loadAcquisitionAnalytics]);

  // FILTERING STATE REMOVED

  // Fetch States on component mount or when modal opens
  useEffect(() => {
    const fetchStates = async () => {
      try {
        setLoadingLocations(true);
        const response = await getStates({ page_size: 100 });
        setStates(response.results || []);
      } catch (err) {
        console.error("Failed to fetch states:", err);
      } finally {
        setLoadingLocations(false);
      }
    };
    
    if (open) {
      fetchStates();
    }
  }, [open]);

  // Fetch LGAs when state changes
  useEffect(() => {
    const fetchLGAs = async () => {
      if (!form.state_id) {
        setLgas([]);
        return;
      }
      
      try {
        setLoadingLocations(true);
        const response = await getLGAs(form.state_id);
        setLgas(response.results || []);
      } catch (err) {
        console.error("Failed to fetch LGAs:", err);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLGAs();
  }, [form.state_id]);

  // Fetch Cities when LGA changes
  useEffect(() => {
    const fetchCities = async () => {
      if (!form.lga_id) {
        setCities([]);
        return;
      }
      
      try {
        setLoadingLocations(true);
        const response = await getCitiesByLGA(form.lga_id);
        setCities(response.results || []);
      } catch (err) {
        console.error("Failed to fetch cities:", err);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchCities();
  }, [form.lga_id]);

  const handleOpen = (row) => {
    if (row) {
      setEditId(row.sid);
      setOriginalRole(row.role || 'customer');
      // If editing, we might need to pre-fill location data if available in row
      // Assuming row might contain location info, otherwise default to empty
      setForm({ 
        username: row.username || '',
        email: row.email || '',
        fullname: row.fullname || '',
        phone: row.phone || '',
        position: row.position || '',
        role: row.role || 'customer',
        is_active: Boolean(row.is_active),
        // Map location fields if they exist in the user object
        state_id: row.state_id || '', 
        lga_id: row.lga_id || '',
        city_id: row.city_id || ''
      });
    } else {
      setEditId(null);
      setOriginalRole('customer');
      setForm(defaultForm);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultForm);
    setEditId(null);
    setOriginalRole('customer');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle boolean values properly for select fields
    let finalValue = value;
    if (name === 'is_active') {
      // Handle both string and boolean values from MenuItem
      if (typeof value === 'string') {
        finalValue = value === 'true';
      } else {
        finalValue = Boolean(value);
      }
    }
    
    setForm({ 
      ...form, 
      [name]: finalValue
    });
  };

  const handleSave = async () => {
    const payload = { ...form };

    if (editId) {
      delete payload.username;
    } else {
      if (!form.username || !form.email) {
        setSnackbar({ open: true, message: t('form.usernameRequired') + ' ' + t('form.emailRequired'), severity: 'error' });
        return;
      }
    }

    try {
      if (editId) {
        const internalRoles = ['staff', 'manager', 'admin'];
        if (internalRoles.includes(form.role)) {
          await dispatch(promoteUser({
            sid: editId,
            userData: { role: form.role, email: form.email, position: form.position }
          })).unwrap();
          await dispatch(updateUser({
            sid: editId,
            userData: { fullname: form.fullname, phone: form.phone, is_active: form.is_active }
          })).unwrap();
        } else if (internalRoles.includes(originalRole)) {
          await dispatch(demoteUser(editId)).unwrap();
          await dispatch(updateUser({ sid: editId, userData: payload })).unwrap();
        } else {
          await dispatch(updateUser({ sid: editId, userData: payload })).unwrap();
        }
        setSnackbar({ open: true, message: t('messages.userUpdated'), severity: 'success' });
        // Refresh the users list to get updated data
        const currentFilters = {
          username: debouncedUsername,
          email: debouncedEmail,
          phone: debouncedPhone,
          role: debouncedRole,
          status: debouncedStatus
        };
        fetchUsersData(page, rowsPerPage, currentFilters);
      } else {
        const result = await dispatch(createUser(form)).unwrap();
        setSnackbar({ open: true, message: t('messages.userAdded'), severity: 'success' });
        // Refresh the users list to get new data
        const currentFilters = {
          username: debouncedUsername,
          email: debouncedEmail,
          phone: debouncedPhone,
          role: debouncedRole,
          status: debouncedStatus
        };
        fetchUsersData(page, rowsPerPage, currentFilters);
      }
      handleClose();
    } catch (error) {
      console.error('Error saving user:', error);
      setSnackbar({ open: true, message: error, severity: 'error' });
    }
  };

  const handleDelete = async (sid) => {
    try {
      await dispatch(deleteUser(sid)).unwrap();
      setSnackbar({ open: true, message: t('messages.userDeleted'), severity: 'info' });
      setDeleteModal({ open: false, userId: null, userName: '' });
      // Refresh the users list after deletion
      const currentFilters = {
        username: debouncedUsername,
        email: debouncedEmail,
        phone: debouncedPhone,
        role: debouncedRole,
        status: debouncedStatus
      };
      fetchUsersData(page, rowsPerPage, currentFilters);
    } catch (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
    }
  };

  // New function to handle delete confirmation
  const handleDeleteClick = (user) => {
    let displayName = '';
    if (user.fullname && user.fullname.trim() !== '') {
      displayName = user.fullname;
    } else if (user.username && user.username.trim() !== '') {
      displayName = user.username;
    } else if (user.email && user.email.trim() !== '') {
      displayName = user.email;
    } else if (user.phone && user.phone.trim() !== '') {
      displayName = user.phone;
    } else {
      displayName = 'this user';
    }

    setDeleteModal({
      open: true,
      userId: user.sid,
      userName: displayName
    });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, userId: null, userName: '' });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.userId) {
      handleDelete(deleteModal.userId);
    }
  };

  const getStatusColor = (status) => {
    return status ? '#4CAF50' : '#F44336';
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return '#DC143C';
      case 'manager': return '#2196F3';
      case 'staff': return '#FF9800';
      case 'merchant': return '#7B1FA2';
      case 'customer': return '#757575';
      case 'user': return '#757575';
      default: return '#757575';
    }
  };

  const formatDateTime = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString();
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString();
  };

  const formatBoolean = (value) => (value ? t('form.yes', 'Yes') : t('form.no', 'No'));

  const getUserDisplayName = (user = {}) => (
    user.fullname || user.username || user.email || user.phone || 'N/A'
  );

  const handleViewUser = async (row) => {
    if (!row?.sid) return;
    setDetailsModal({ open: true, user: row, loading: true });
    try {
      const userDetails = await getUser(row.sid);
      setDetailsModal({ open: true, user: userDetails, loading: false });
    } catch (viewError) {
      setDetailsModal({ open: false, user: null, loading: false });
      setSnackbar({
        open: true,
        message: viewError.message || 'Failed to load user details',
        severity: 'error'
      });
    }
  };

  const handleCloseDetails = () => {
    setDetailsModal({ open: false, user: null, loading: false });
  };

  const renderDetailItem = (label, value) => (
    <Grid size={{ xs: 12, sm: 6 }} key={label}>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, minHeight: 72 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
          {value || 'N/A'}
        </Typography>
      </Box>
    </Grid>
  );

  // Add serial numbers to users for the current page
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    { field: 'sn', headerName: t('table.sn'), width: 80, sortable: false, filterable: false },

    { 
      field: 'fullname', 
      headerName: t('table.fullname'), 
      flex: 1, 
      minWidth: 150,
      renderCell: (params) => {
        const fullname = params.row?.fullname;
        const displayName = fullname && fullname.trim() !== '' ? fullname : 'N/A';
        return <span>{displayName}</span>;
      }
    },
    { 
      field: 'username', 
      headerName: t('table.username'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const username = params.row?.username;
        const displayUsername = username && username.trim() !== '' ? username : 'N/A';
        return <span>{displayUsername}</span>;
      }
    },
    { 
      field: 'email', 
      headerName: t('table.email'), 
      flex: 1, 
      minWidth: 200,
      renderCell: (params) => {
        const email = params.row?.email;
        const displayEmail = email && email.trim() !== '' ? email : 'N/A';
        return <span>{displayEmail}</span>;
      }
    },
    { 
      field: 'phone', 
      headerName: t('table.phone'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const phone = params.row?.phone;
        const displayPhone = phone && phone.trim() !== '' ? phone : 'N/A';
        return <span>{displayPhone}</span>;
      }
    },
    { 
      field: 'position', 
      headerName: t('table.position'), 
      flex: 1, 
      minWidth: 150,
      renderCell: (params) => {
        const position = params.row?.position;
        return <span>{position && position.trim() !== '' ? position : 'N/A'}</span>;
      }
    },
    { 
      field: 'role', 
      headerName: t('table.role'), 
      flex: 1, 
      minWidth: 100,
      renderCell: (params) => {
        const role = params.row?.role || 'user';
        const roleLabel = t(`roles.${role}`, role.charAt(0).toUpperCase() + role.slice(1));
        return (
          <Chip
            label={roleLabel}
            size="small"
            sx={{
              backgroundColor: getRoleColor(role),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    { 
      field: 'is_active', 
      headerName: t('table.status'), 
      flex: 1, 
      minWidth: 100,
      renderCell: (params) => {
        const isActive = params.row?.is_active;
        // Handle boolean conversion properly
        let activeStatus = false;
        if (typeof isActive === 'boolean') {
          activeStatus = isActive;
        } else if (typeof isActive === 'string') {
          activeStatus = isActive.toLowerCase() === 'true';
        } else if (typeof isActive === 'number') {
          activeStatus = isActive === 1;
        }
        
        return (
          <Chip
            label={activeStatus ? t('common:status.active') : t('common:status.inactive')}
            size="small"
            sx={{
              backgroundColor: getStatusColor(activeStatus),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        );
      }
    },
    { 
      field: 'created_at', 
      headerName: t('table.joinDate'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => {
        const createdAt = params.row?.created_at;
        if (!createdAt) return <span>N/A</span>;
        
        try {
          const date = new Date(createdAt);
          if (isNaN(date.getTime())) {
            return <span>N/A</span>;
          }
          return <span>{date.toLocaleDateString()}</span>;
        } catch (error) {
          console.error('Error formatting date:', error, createdAt);
          return <span>N/A</span>;
        }
      }
    },
    {
      field: 'actions',
      headerName: t('table.actions'),
      width: 160,
      sortable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        return (
          <Box>
            <Tooltip title="View">
              <IconButton color="info" onClick={() => handleViewUser(params.row)}>
                <Visibility />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton color="primary" onClick={() => handleOpen(params.row)}>
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton color="error" onClick={() => handleDeleteClick(params.row)}>
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  if (loading && users.length === 0) {
    return (
      <>
        <PageMeta
          title={t('title')}
          description="Manage users for the Checkstall Import platform, including roles and statuses."
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{t('search.loadingUsers')}</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <PageMeta
        title={t('title')}
        description="Manage users for the Checkstall Import platform, including roles and statuses."
      />
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            {t('common:buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
            onClick={() => handleOpen()}
          >
            {t('addUser')}
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" sx={{ color: '#DC143C', fontWeight: 'bold' }}>
              User Acquisition Analytics
            </Typography>
            <TextField
              select
              size="small"
              value={analyticsPeriod}
              onChange={(e) => setAnalyticsPeriod(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Total Users</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{totals.all_time || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Last 7 Days</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{totals.last_7_days || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Last 30 Days</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{totals.last_30_days || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Month-on-Month</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{comparisons.month_on_month?.current || 0}</Typography>
                  <Typography variant="body2" sx={{ color: getComparisonColor(comparisons.month_on_month?.change_percent), fontWeight: 600 }}>
                    {formatPercent(comparisons.month_on_month?.change_percent)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Quarter-on-Quarter
                  </Typography>
                  <Typography variant="body2">
                    Current: {comparisons.quarter_on_quarter?.current || 0} • Previous: {comparisons.quarter_on_quarter?.previous || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: getComparisonColor(comparisons.quarter_on_quarter?.change_percent), fontWeight: 600 }}>
                    {formatPercent(comparisons.quarter_on_quarter?.change_percent)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Year-on-Year
                  </Typography>
                  <Typography variant="body2">
                    Current: {comparisons.year_on_year?.current || 0} • Previous: {comparisons.year_on_year?.previous || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: getComparisonColor(comparisons.year_on_year?.change_percent), fontWeight: 600 }}>
                    {formatPercent(comparisons.year_on_year?.change_percent)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ height: 320, width: '100%' }}>
            {analyticsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <CircularProgress size={26} />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="signups" stroke="#DC143C" strokeWidth={2} dot />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* Dynamic Filter Section */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box>
              <Typography variant="h6" sx={{ color: '#DC143C', fontWeight: 'bold' }}>
                {t('search.title')}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {t('search.description')}
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              flexWrap: 'wrap'
            }}>
              <Typography variant="body2" color="textSecondary">
                {t('search.showingUsers', { count: users.length })}
                {hasActiveFilters && (
                  <span> {t('search.filtered')}</span>
                )}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                sx={{ borderColor: '#DC143C', color: '#DC143C' }}
              >
                {t('search.clearFilters')}
              </Button>
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('search.usernameLabel')}
                value={filters.username}
                onChange={(e) => handleFilterChange('username', e.target.value)}
                size="small"
                fullWidth
                placeholder={t('search.placeholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('search.emailLabel')}
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                size="small"
                fullWidth
                placeholder={t('search.placeholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('search.phoneLabel')}
                value={filters.phone}
                onChange={(e) => handleFilterChange('phone', e.target.value)}
                size="small"
                fullWidth
                placeholder={t('search.placeholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label={t('filterLabels.byRole')}
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">{t('filterLabels.allRoles')}</MenuItem>
                <MenuItem value="admin">{t('roles.admin')}</MenuItem>
                <MenuItem value="manager">{t('roles.manager')}</MenuItem>
                <MenuItem value="staff">{t('roles.staff')}</MenuItem>
                <MenuItem value="user">{t('roles.user')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label={t('filterLabels.byStatus')}
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">{t('filterLabels.allStatus')}</MenuItem>
                <MenuItem value="active">{t('common:status.active')}</MenuItem>
                <MenuItem value="inactive">{t('common:status.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Users Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
                   {users.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  {t('search.noUsersFound')}
                </Typography>
                <Button variant="outlined" onClick={handleRefresh}>
                  {t('search.refreshData')}
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(users, page, rowsPerPage)}
                columns={columns}
                getRowId={(row) => row.sid}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1200, // Ensure minimum width for all columns
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: theme => theme.palette.primary.main,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('pagination.page')} {totalPages === 0 ? 0 : page + 1} {t('pagination.of')} {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('pagination.rows')}
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>{editId ? t('editUser') : t('addUser')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label={t('form.username')} name="username" value={form.username} onChange={handleChange} required fullWidth />
            <TextField label={t('form.fullname')} name="fullname" value={form.fullname} onChange={handleChange} fullWidth />
            <TextField label={t('form.position')} name="position" value={form.position} onChange={handleChange} fullWidth />
            <TextField label={t('form.email')} name="email" value={form.email} onChange={handleChange} type="email" required fullWidth />
            <TextField label={t('form.phone')} name="phone" value={form.phone} onChange={handleChange} fullWidth />
            <TextField
              select
              label={t('form.role')}
              name="role"
              value={form.role}
              onChange={handleChange}
              required
              fullWidth
            >
              <MenuItem value="admin">{t('roles.admin')}</MenuItem>
              <MenuItem value="manager">{t('roles.manager')}</MenuItem>
              <MenuItem value="staff">{t('roles.staff')}</MenuItem>
              <MenuItem value="customer">{t('roles.customer')}</MenuItem>
            </TextField>
            <TextField
              select
              label={t('form.isActive')}
              name="is_active"
              value={form.is_active}
              onChange={handleChange}
              required
              fullWidth
            >
              <MenuItem value={true}>{t('common:status.active')}</MenuItem>
              <MenuItem value={false}>{t('common:status.inactive')}</MenuItem>
            </TextField>

            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, color: '#666', fontWeight: 'bold' }}>
              Location Details (Optional)
            </Typography>

            <TextField
              select
              label="State"
              name="state_id"
              value={form.state_id}
              onChange={(e) => {
                setForm({ ...form, state_id: e.target.value, lga_id: '', city_id: '' });
              }}
              fullWidth
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {states.map((state) => (
                <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="LGA"
              name="lga_id"
              value={form.lga_id}
              onChange={(e) => {
                setForm({ ...form, lga_id: e.target.value, city_id: '' });
              }}
              fullWidth
              disabled={!form.state_id}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {lgas.map((lga) => (
                <MenuItem key={lga.id} value={lga.id}>{lga.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="City"
              name="city_id"
              value={form.city_id}
              onChange={handleChange}
              fullWidth
              disabled={!form.lga_id}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common:buttons.cancel')}</Button>
          <Button onClick={handleSave} variant="contained" sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}>{editId ? t('common:buttons.update') : t('common:buttons.add')}</Button>
        </DialogActions>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={detailsModal.open} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          User Details
        </DialogTitle>
        <DialogContent dividers>
          {detailsModal.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading user details...</Typography>
            </Box>
          ) : detailsModal.user ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar
                  src={detailsModal.user.avatar_url || undefined}
                  sx={{ width: 64, height: 64, bgcolor: '#DC143C' }}
                >
                  <Person />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                    {getUserDisplayName(detailsModal.user)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                    {detailsModal.user.email || detailsModal.user.phone || 'No contact details'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={t(`roles.${detailsModal.user.role}`, detailsModal.user.role || 'N/A')}
                      size="small"
                      sx={{ backgroundColor: getRoleColor(detailsModal.user.role), color: 'white', fontWeight: 'bold' }}
                    />
                    <Chip
                      label={detailsModal.user.is_active ? t('common:status.active') : t('common:status.inactive')}
                      size="small"
                      sx={{ backgroundColor: getStatusColor(detailsModal.user.is_active), color: 'white', fontWeight: 'bold' }}
                    />
                    {detailsModal.user.pending_deletion && (
                      <Chip label="Pending deletion" color="warning" size="small" />
                    )}
                  </Box>
                </Box>
              </Box>

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Identity
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {renderDetailItem('Public ID', detailsModal.user.sid)}
                {renderDetailItem('Username', detailsModal.user.username)}
                {renderDetailItem('Full Name', detailsModal.user.fullname)}
                {renderDetailItem('Email', detailsModal.user.email)}
                {renderDetailItem('Phone', detailsModal.user.phone)}
                {renderDetailItem('Warehouse Bin Code', detailsModal.user.bin_code)}
                {renderDetailItem('Position', detailsModal.user.position)}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Account & Access
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {renderDetailItem('Role', t(`roles.${detailsModal.user.role}`, detailsModal.user.role || 'N/A'))}
                {renderDetailItem('Active', formatBoolean(detailsModal.user.is_active))}
                {renderDetailItem('Staff Account', formatBoolean(detailsModal.user.is_staff))}
                {renderDetailItem('Superuser', formatBoolean(detailsModal.user.is_superuser))}
                {renderDetailItem('Admin Access', formatBoolean(detailsModal.user.is_admin))}
                {renderDetailItem('Demo/Internal Account', formatBoolean(detailsModal.user.is_demo_account))}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Activity
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {renderDetailItem('Last Active', formatDateTime(detailsModal.user.last_active_at || detailsModal.user.last_login || detailsModal.user.current_login))}
                {renderDetailItem('Last Login', formatDateTime(detailsModal.user.last_login))}
                {renderDetailItem('Current Login', formatDateTime(detailsModal.user.current_login))}
                {renderDetailItem('Joined', formatDateTime(detailsModal.user.created_at))}
                {renderDetailItem('First Seen', formatDateTime(detailsModal.user.first_seen_at))}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Acquisition & Lifecycle
              </Typography>
              <Grid container spacing={2}>
                {renderDetailItem('Acquisition Source', detailsModal.user.acquisition_source)}
                {renderDetailItem('Referral Source', detailsModal.user.referral_source)}
                {renderDetailItem('Primary Geo Snapshot', detailsModal.user.primary_geo_snapshot)}
                {renderDetailItem('Deletion Requested At', formatDate(detailsModal.user.deletion_requested_at))}
              </Grid>
            </Box>
          ) : (
            <Typography color="text.secondary">No user details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>{t('common:buttons.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteModal.open} 
        onClose={handleDeleteCancel}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          color: '#DC143C', 
          fontWeight: 'bold',
          pb: 1
        }}>
          <Warning sx={{ color: '#DC143C', fontSize: 28 }} />
          {t('deleteUser')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#333' }}>
            {t('messages.confirmDelete', { name: deleteModal.userName })}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ 
            backgroundColor: '#FFF3E0', 
            p: 2, 
            borderRadius: 1, 
            border: '1px solid #FFB74D',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Warning sx={{ color: '#F57C00', fontSize: 20 }} />
            {t('messages.deleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{ 
              borderColor: '#666', 
              color: '#666',
              '&:hover': { 
                borderColor: '#333', 
                backgroundColor: '#f5f5f5' 
              }
            }}
          >
            {t('common:buttons.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ 
              backgroundColor: '#DC143C', 
              '&:hover': { backgroundColor: '#B91C3C' },
              px: 3
            }}
            startIcon={<Delete />}
          >
            {t('deleteUser')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </>
  );
};

export default Users; 
